import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Building2, Plus, Trash2, Loader2, CheckCircle2, Search, X } from 'lucide-react';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { useUploadStore } from '../stores/uploadStore';
import { useObvestilaStore } from '../stores/obvestilaStore'; // [PATCH obvestila-dokumenti v1]
import { IzbrisBazeDialog } from '../components/IzbrisBazeDialog';

export function MojaPodjetjaPage() {
  const navigate = useNavigate();
  const { selectedCompany, setSelectedCompany } = useAuthStore();
  const poPodjetju = useUploadStore((s: any) => s.poPodjetju);
  const sopPoPodjetju = useUploadStore((s: any) => s.sopPoPodjetju); // [PATCH sop-napredek-fe v1]
  const obvestila = useObvestilaStore((s: any) => s.vnosi); // [PATCH obvestila-dokumenti v1]

  const [podjetja, setPodjetja] = useState<any[]>([]);
  const [nalagam, setNalagam] = useState(true);
  const [izbrisBaza, setIzbrisBaza] = useState<any | null>(null);
  const [smeOdpirati, setSmeOdpirati] = useState(false); // [PATCH odpiranje-baz-fe v1]
  const [q, setQ] = useState(''); // [PATCH podjetja-isci-abeceda v1] iskalni niz

  // [PATCH odpiranje-baz-fe v1] pravica do odpiranja nove baze
  // (superadmin ALI can_create_companies) -- vzorec iz DashboardLayout
  useEffect(() => {
    api.get('/auth/me')
      .then((r: any) => setSmeOdpirati(
        !!(r.data.is_superadmin || r.data.can_create_companies)))
      .catch(() => setSmeOdpirati(false));
  }, []);

  const naloziPodjetja = () => {
    setNalagam(true);
    api.get('/companies/')
      .then((r: any) => setPodjetja(r.data || []))
      .catch(() => {})
      .finally(() => setNalagam(false));
  };
  useEffect(() => { naloziPodjetja(); }, []);

  /* [PATCH podjetja-isci-abeceda v1] normalizacija: male crke + NFD odstranitev
     diakritike, da "slas" najde SLASCICARNA in "ziri" najde ZIRI. */
  const _normP = (v: any) =>
    String(v || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  /* [PATCH podjetja-isci-abeceda v1] izpeljan seznam za prikaz: abecedno po
     nazivu (slovenska kolacija) + filter po nazivu IN davcni. Vir `podjetja`
     ostane nedotaknjen, zato se osvezitev in izbris obnasata kot prej. */
  const podjetjaPrikaz = useMemo(() => {
    const urejena = [...podjetja].sort((a: any, b: any) =>
      String(a?.name || '').localeCompare(String(b?.name || ''), 'sl', { numeric: true })
    );
    const niz = _normP(q).trim();
    if (!niz) return urejena;
    const zetoni = niz.split(/\s+/).filter(Boolean);
    return urejena.filter((c: any) => {
      const naziv = _normP(c?.name);
      const dav = _normP(c?.tax_number || c?.vat_number).replace(/[^0-9a-z]/g, '');
      const davSt = dav.replace(/[^0-9]/g, '');
      return zetoni.every((z: string) => {
        if (naziv.includes(z)) return true;
        if (dav && dav.includes(z)) return true;
        const zSt = z.replace(/[^0-9]/g, '');   // "si33013926" -> "33013926"
        return !!zSt && !!davSt && davSt.includes(zSt);
      });
    });
  }, [podjetja, q]);

  return (
    <div className="max-w-5xl mx-auto">
      {/* glava */}
      <div className="flex items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Moja podjetja</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Podjetja, do katerih imaš dostop</p>
        </div>
        {/* [PATCH odpiranje-baz-fe v1] gumb samo za tiste s pravico */}
        {smeOdpirati && (
        <button
          onClick={() => navigate('/onboarding')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition shrink-0"
        >
          <Plus className="w-4 h-4" /> Dodaj podjetje
        </button>
        )}
      </div>

      {/* [PATCH podjetja-isci-abeceda v1] iskalnik po nazivu in davcni stevilki */}
      {!nalagam && podjetja.length > 0 && (
        <div className="mb-4">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') setQ(''); }}
              placeholder="Išči po nazivu ali davčni številki …"
              className="w-full pl-9 pr-9 py-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900"
            />
            {q && (
              <button
                onClick={() => setQ('')}
                title="Počisti"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {q && (
            <div className="mt-1 text-xs text-gray-400">
              {podjetjaPrikaz.length} od {podjetja.length}
            </div>
          )}
        </div>
      )}

      {nalagam ? (
        <div className="flex items-center justify-center gap-2 text-gray-400 py-16">
          <Loader2 className="w-5 h-5 animate-spin" /> Nalagam podjetja…
        </div>
      ) : podjetja.length === 0 ? (
        <div className="text-center py-16">
          <Building2 className="w-10 h-10 text-gray-300 dark:text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">
            {/* [PATCH odpiranje-baz-fe v1] */}
            {smeOdpirati
              ? 'Ni podjetij. Klikni „Dodaj podjetje" za začetek.'
              : 'Ni podjetij. Za odpiranje nove baze se obrni na skrbnika računovodskega servisa.'}
          </p>
        </div>
      ) : podjetjaPrikaz.length === 0 ? (
        /* [PATCH podjetja-isci-abeceda v1] prazen rezultat iskanja */
        <div className="text-center py-16">
          <Building2 className="w-10 h-10 text-gray-300 dark:text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">
            Ni zadetkov za <span className="font-medium">{q}</span>
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {podjetjaPrikaz.map((c: any) => {
            const u = poPodjetju[c.id];
            const aktiven = !!(u && u.aktiven);
            const koncano = !!(u && !u.aktiven && u.skupaj > 0);
            const pct = u && u.skupaj > 0 ? Math.round((u.koncano / u.skupaj) * 100) : 0;
            const uspesno = u ? u.koncano - u.podvojeni - u.napake - (u.izpusceni || 0) : 0; /* [PATCH sop-napredek-fe v1] izpusceni (predracun/ponudba) niso uspesni — uskladitev z uploadStore */
            /* [PATCH sop-napredek-fe v1] napredek SOPA (gumb "Uvoz"): tece na
               strezniku, zato ga store polla in prezivi zaprtje okna. */
            const sopSt = sopPoPodjetju[c.id];
            const sopD: any = (sopSt && sopSt.podatki) || {};
            const sopSkupaj = sopD.st_dokumentov || 0;
            const sopObdelano = (sopD.st_koncano || 0) + (sopD.st_preskoceno || 0) + (sopD.st_napak || 0);
            const sopAktiven = !!(sopSt && sopSt.aktiven);
            const sopKoncan = !!(sopSt && !sopSt.aktiven && sopSkupaj > 0);
            const sopPct = sopSkupaj > 0 ? Math.round((sopObdelano / sopSkupaj) * 100) : 0;
            const jeIzbrano = selectedCompany?.id === c.id;
            const noviVnosi = obvestila.filter((v: any) => v.company_id === c.id); // [PATCH obvestila-dokumenti v1]
            const stNovih = noviVnosi.reduce((a: number, v: any) => a + (v.st_dokumentov || 0), 0);
            return (
              <div
                key={c.id}
                className={clsx(
                  'rounded-xl border bg-white dark:bg-gray-900 p-4 transition hover:shadow-md',
                  jeIzbrano
                    ? 'border-blue-300 dark:border-blue-800 ring-1 ring-blue-200 dark:ring-blue-900'
                    : 'border-gray-200 dark:border-gray-800'
                )}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-950 flex items-center justify-center shrink-0">
                    <Building2 className="w-5 h-5 text-blue-500" />
                  </div>
                  <button
                    onClick={() => setSelectedCompany(c)}
                    className="text-left min-w-0 flex-1"
                    title="Izberi to podjetje"
                  >
                    <div className="font-semibold text-gray-900 dark:text-white truncate">{c.name}</div>
                    <div className="text-xs text-gray-400 font-mono">{c.tax_number || c.vat_number || '—'}</div>
                    {stNovih > 0 && ( /* [PATCH obvestila-dokumenti v1] znacka novih dokumentov */
                      <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                          {stNovih} {stNovih % 100 === 1 ? 'nov' : stNovih % 100 === 2 ? 'nova' : (stNovih % 100 === 3 || stNovih % 100 === 4) ? 'novi' : 'novih'}
                        </span>
                        <span className="text-[11px] text-gray-400">
                          {noviVnosi.map((v: any) => {
                            const kr: any = { prejeti_racun: 'prejeti', izdani_racun: 'izdani', bancni_izpisek: 'izpiski', blagajna: 'blagajna' };
                            return v.st_dokumentov + ' ' + (kr[v.zavihek] || v.zavihek);
                          }).join(' \u00b7 ')}
                        </span>
                      </div>
                    )}
                  </button>
                  <button
                    onClick={() => setIzbrisBaza(c)}
                    title="Izbriši bazo"
                    className="p-1.5 rounded-lg text-gray-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 transition shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* status nalaganja */}
                <div className="mt-3 h-6 flex items-center">
                  {/* [PATCH sop-napredek-fe v1] sop ima prednost pri izrisu; hkraten
                      posamicni + sop uvoz je onemogocen v DokumentiPage. */}
                  {sopAktiven ? (
                    <div className="flex items-center gap-2 w-full">
                      <Loader2 className="w-4 h-4 text-blue-500 animate-spin shrink-0" />
                      <span className="text-xs font-medium text-blue-700 dark:text-blue-300 whitespace-nowrap">
                        Uvažam {sopObdelano}/{sopSkupaj}
                      </span>
                      <div className="flex-1 h-1 bg-blue-100 dark:bg-blue-900 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 transition-all" style={{ width: `${sopPct}%` }} />
                      </div>
                    </div>
                  ) : sopKoncan ? (
                    <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                      <CheckCircle2 className="w-4 h-4 shrink-0" />
                      <span className="text-xs font-medium">Uvoz končan</span>
                      <span className="text-xs text-gray-400">
                        ({sopD.st_koncano || 0} uvoženih{sopD.st_preskoceno ? `, ${sopD.st_preskoceno} preskočenih` : ''}{sopD.st_napak ? `, ${sopD.st_napak} napak` : ''})
                      </span>
                    </div>
                  ) : aktiven ? (
                    <div className="flex items-center gap-2 w-full">
                      <Loader2 className="w-4 h-4 text-blue-500 animate-spin shrink-0" />
                      <span className="text-xs font-medium text-blue-700 dark:text-blue-300 whitespace-nowrap">
                        Uvažam {u.koncano}/{u.skupaj}
                      </span>
                      <div className="flex-1 h-1 bg-blue-100 dark:bg-blue-900 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  ) : koncano ? (
                    <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                      <CheckCircle2 className="w-4 h-4 shrink-0" />
                      <span className="text-xs font-medium">Dokumenti naloženi</span>
                      {(u.podvojeni > 0 || u.napake > 0) && (
                        <span className="text-xs text-gray-400">
                          ({uspesno} ok{u.podvojeni ? `, ${u.podvojeni} podv.` : ''}{u.napake ? `, ${u.napake} nap.` : ''})
                        </span>
                      )}
                    </div>
                  ) : jeIzbrano ? (
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">Trenutno izbrano</span>
                  ) : (
                    <span className="text-xs text-gray-300 dark:text-gray-600">—</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {izbrisBaza && (
        <IzbrisBazeDialog
          baza={izbrisBaza}
          onClose={() => setIzbrisBaza(null)}
          onDeleted={(id: string) => {
            if (selectedCompany?.id === id) setSelectedCompany(null);
            naloziPodjetja();
          }}
        />
      )}
    </div>
  );
}

export default MojaPodjetjaPage;
